'use client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Link from 'next/link'
import Image from 'next/legacy/image'
import { footerData } from '@/data/landing-page/footerData'
import { Icons } from '../icons'
import { useNewsletterMutation } from '@/src/mutations/landing/newsletter'
import { toast } from 'sonner'
import { FormControl, FormErrorMessage } from '@chakra-ui/react'
import { FormProvider, useForm } from 'react-hook-form'
import { useState } from 'react'
import { Facebook, Instagram, Linkedin } from 'lucide-react'
interface NewsLetterTs {
  email: string
}
export default function Footer() {
  const copyrightYear = 2026
  const methods = useForm<NewsLetterTs>()
  const [email, setEmail] = useState('')
  const [isSubmitDone, setSubmitDone] = useState(false)
  const newsletterMutation = useNewsletterMutation()
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = methods

  const footerItemMap = new Map(
    footerData.sections.flatMap((section) =>
      section.items.map((item) => [item.name, item] as const)
    )
  )

  const exploreItems = ['Login', 'About Us', 'Viewer Earnings', "Creator's Guide"]
    .map((name) => footerItemMap.get(name))
    .filter((item): item is NonNullable<(typeof footerData.sections)[number]['items'][number]> => !!item)

  const resourcesItems = ['FAQs', 'Blog', 'Creator Earnings Assurance Program']
    .map((name) => footerItemMap.get(name))
    .filter((item): item is NonNullable<(typeof footerData.sections)[number]['items'][number]> => !!item)

  const legalItems = footerData.sections.find((section) => section.label === 'Legal')?.items || []
  const socialItems = ['LinkedIn', 'Instagram', 'Facebook']
    .map((name) => footerItemMap.get(name))
    .filter((item): item is NonNullable<(typeof footerData.sections)[number]['items'][number]> => !!item)
  const discordItem = footerItemMap.get('Join our Discord')

  const socialIcons = {
    LinkedIn: Linkedin,
    Instagram,
    Facebook,
  } as const

  const onSubmit = (data: NewsLetterTs) => {
    newsletterMutation.mutate(data, {
      onSuccess: () => {
        toast.success(
          'You have successfully joined our mailing list. Stay tuned for exciting updates!'
        )
        reset()
        setEmail('')
        setSubmitDone(true)
        setTimeout(() => {
          setSubmitDone(false)
        }, 5000)
      },
      onError: (error) => {
        toast.error('Failed to Join')
        setError('root', {
          type: 'manual',
          message: error.message,
        })
      },
    })
  }

  return (
    <div className="w-full bg-[#181818] px-4 py-8 sm:px-6 lg:px-8">
      <footer className="mx-auto w-full rounded-[22px] border border-white/10 bg-[#242426] px-5 py-6 text-white shadow-[0_20px_50px_rgba(0,0,0,0.28)] sm:px-8 sm:py-8">
        <div
          className="flex flex-col gap-6 border-b border-white/10 pb-7 min-[561px]:flex-row min-[561px]:items-center min-[561px]:justify-between min-[561px]:gap-5 min-[561px]:pb-6"
          data-aos="fade-up"
          data-aos-anchor-placement="top-bottom"
        >
          <div className="flex flex-row items-center gap-3 max-[786px]:flex-col">
            <div className="relative h-[50px] w-[188px] text-lg font-semibold">
              <Link href="/home">
                <Image src="/lykstage-logo.svg" alt="logo" layout="fill" />
              </Link>
            </div>
            <p className="text-sm text-white/70 pl-2 border-l border-white/10 max-[786px]:border-l-0 max-[786px]:pl-0">{footerData.description}</p>
          </div>

          <div className="w-full min-[561px]:w-auto flex flex-row gap-3 items-center  max-[786px]:flex-col max-[786px]:items-start">
            <p className="text-sm text-white/80">
              Join our mailing list
            </p>

            {!isSubmitDone ? (
              <FormProvider {...methods}>
                <form name="newsletter" className="max-[561px]:w-full" onSubmit={handleSubmit(onSubmit)}>
                  <FormControl isRequired isInvalid={!!errors.email} display={'block'}>
                    <div className="flex w-full flex-col gap-2 min-[561px]:flex-row min-[561px]:items-center min-[561px]:gap-3">
                      <div className="relative w-full min-[561px]:min-w-[200px] min-[901px]:min-w-[216px]">
                        <Input
                          id="emailNewsletter"
                          {...register('email', {
                            required: 'Email is required',
                          })}
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Your email"
                          className="h-11 w-full rounded-[10px] border-bordergray bg-white py-2 pl-10 pr-3 text-black"
                        />
                        <Icons.Email className="absolute left-3 top-1/2 h-[14px] w-[14px] -translate-y-1/2 text-bordergray" />
                      </div>

                      <Button
                        type="submit"
                        isLoading={newsletterMutation.isPending}
                        className="h-11 rounded-[10px] bg-red px-6 text-[13px] font-semibold text-white transition-all duration-300 hover:bg-[#de7874] max-[560px]:w-full"
                      >
                        Subscribe
                      </Button>
                    </div>
                    <FormErrorMessage>{errors.email && errors.email?.message}</FormErrorMessage>
                  </FormControl>
                </form>
              </FormProvider>
            ) : (
              <div className="max-[560px]:w-full">
                <Button className="h-11 w-full rounded-[10px] bg-green px-6 text-[13px] font-semibold text-white transition-all duration-300 min-[561px]:w-auto">
                  Subscribed
                  <Image
                    src="/landing-page/check-small.svg"
                    alt="check"
                    width={24}
                    height={24}
                  />
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 py-5 max-[560px]:divide-y max-[560px]:divide-white/10 min-[561px]:grid-cols-4 min-[561px]:gap-5 min-[901px]:gap-10 min-[901px]:py-8">
          <div
            className="flex min-w-0 flex-col gap-3 py-4 min-[561px]:py-0"
            data-aos="fade-up"
            data-aos-anchor-placement="top-bottom"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-white/45 min-[901px]:text-[13px]">
              Explore
            </span>
            <div className="flex flex-col gap-2 text-[13px] text-white min-[561px]:gap-1.5 min-[561px]:text-[12px] min-[901px]:gap-2 min-[901px]:text-[15px]">
              {exploreItems.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="transition-colors duration-300 hover:text-green"
                  target={item.target ? item.target : '_self'}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          <div
            className="flex min-w-0 flex-col gap-3 py-4 min-[561px]:py-0"
            data-aos="fade-up"
            data-aos-anchor-placement="top-bottom"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-white/45 min-[901px]:text-[13px]">
              Resources
            </span>
            <div className="flex flex-col gap-2 text-[13px] text-white min-[561px]:gap-1.5 min-[561px]:text-[12px] min-[901px]:gap-2 min-[901px]:text-[15px]">
              {resourcesItems.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="transition-colors duration-300 hover:text-green"
                  target={item.target ? item.target : '_self'}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          <div
            className="flex min-w-0 flex-col gap-3 py-4 min-[561px]:py-0"
            data-aos="fade-up"
            data-aos-anchor-placement="top-bottom"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-white/45 min-[901px]:text-[13px]">
              Legal
            </span>
            <div className="flex flex-col gap-2 text-[13px] text-white min-[561px]:gap-1.5 min-[561px]:text-[12px] min-[901px]:gap-2 min-[901px]:text-[15px]">
              {legalItems.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="transition-colors duration-300 hover:text-green"
                  target={item.target ? item.target : '_self'}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          <div
            className="flex min-w-0 flex-col gap-3 py-4 min-[561px]:py-0"
            data-aos="fade-up"
            data-aos-anchor-placement="top-bottom"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-white/45 min-[901px]:text-[13px]">
              Community
            </span>

            <div className="flex items-center gap-2">
              {socialItems.map((item) => {
                const IconComponent = socialIcons[item.name as keyof typeof socialIcons]
                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    target={item.target ? item.target : '_blank'}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-white/10 bg-white/5 text-white/75 transition-all duration-300 hover:border-white/20 hover:text-white min-[901px]:h-10 min-[901px]:w-10"
                    aria-label={item.name}
                  >
                    <IconComponent className="h-[16px] w-[16px] min-[901px]:h-[18px] min-[901px]:w-[18px]" />
                  </Link>
                )
              })}
            </div>

            {discordItem ? (
              <Link
                href={discordItem.href}
                target={discordItem.target ? discordItem.target : '_blank'}
                className="mt-1 inline-flex h-10 w-full items-center justify-center gap-2 rounded-[11px] bg-[#5865F2] px-4 text-[13px] font-semibold text-white shadow-[0_8px_24px_rgba(88,101,242,0.35)] transition-all duration-300 hover:bg-[#4f5ce2] min-[561px]:h-9 min-[561px]:text-[12px] min-[901px]:h-10 min-[901px]:text-[14px]"
              >
                <Icons.Discord className="h-4 w-4" />
                Join our Discord
              </Link>
            ) : null}
          </div>
        </div>

        <div className="border-t border-white/10 pt-5 text-[12px] text-white/60 min-[901px]:pt-6 min-[901px]:text-sm">
          &copy; {footerData.brand} {copyrightYear}{' '}
          <span className="text-[12px] opacity-70">
           - LYKSTAGE copyright and intellectual property are owned by LYK Inc., a Delaware-based corporation.
          </span>
        </div>
      </footer>
    </div>
  )
}
